import { CalendarDate, RegionId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { DateRangeControl, DateRangeValue } from "components/reports/date-presets";
import { defaultCardStyle } from "components/ui/card";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useRegionId } from "store/selectors/hooks";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";
import { trpc } from "../../rpc/trpc";

type RouterOutput = inferRouterOutputs<AppRouter>;
type ReportResult = RouterOutput["timeTrackingReport"]["report"];
type ClientGroup = ReportResult["clients"][number];
type ReportEntry = ClientGroup["entries"][number];

function formatDurationHumanReadable(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

function escapeCsvValue(value: string | number): string {
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function triggerCsvDownload(rows: (string | number)[][], filename: string): void {
    const csvContent = rows.map(row => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

function formatEntryDate(isoDate: string): string {
    const d = new Date(isoDate);
    return d.toLocaleDateString();
}

function generateClientCsv(entries: ReportEntry[]): (string | number)[][] {
    const header = ["Date", "Duration", "User", "Notes"];
    const rows: (string | number)[][] = [header];
    for (const entry of entries) {
        rows.push([
            formatEntryDate(entry.date),
            formatDurationHumanReadable(entry.durationMinutes),
            entry.userName,
            entry.note ?? "",
        ]);
    }
    return rows;
}

function generateFullCsv(clients: ClientGroup[]): (string | number)[][] {
    const header = ["Client", "Date", "Duration", "User", "Notes"];
    const rows: (string | number)[][] = [header];
    for (const client of clients) {
        for (const entry of client.entries) {
            rows.push([
                client.clientName,
                formatEntryDate(entry.date),
                formatDurationHumanReadable(entry.durationMinutes),
                entry.userName,
                entry.note ?? "",
            ]);
        }
    }
    return rows;
}

function buildClientCsvFilename(
    clientName: string,
    startDate: string,
    endDate: string,
): string {
    const slug = clientName.replace(/\s+/g, "-").toLowerCase();
    return `time-tracking-${slug}-${startDate}-to-${endDate}.csv`;
}

export const TimeTracking: React.FC = () => {
    const [dateRange, setDateRange] = React.useState<DateRangeValue>(() => {
        const today = CalendarDate.today;
        const startDate = new CalendarDate(today.year, today.month, 1);
        return { startDate, endDate: today, activePreset: "This Month" };
    });
    const regionId = useRegionId();
    const { startDate, endDate } = dateRange;
    const validQuery = regionId && startDate && endDate;

    return (
        <Stage>
            <PageHeader title="Time Tracking" stackBreakpoint="md">
                <DateRangeControl value={dateRange} onChange={setDateRange} />
            </PageHeader>
            <ContentContainer>
                {validQuery ? (
                    <TimeTrackingContent
                        startDate={startDate.toString()}
                        endDate={endDate.toString()}
                        regionId={regionId}
                    />
                ) : null}
            </ContentContainer>
        </Stage>
    );
};

const TimeTrackingContent: React.FC<{
    startDate: string;
    endDate: string;
    regionId: RegionId;
}> = ({ startDate, endDate, regionId }) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery(
        trpcClient.timeTrackingReport.report.queryOptions({
            startDate,
            endDate,
            regionId,
        }),
    );

    if (query.isLoading) {
        return (
            <ContentFull>
                <Spinner />
            </ContentFull>
        );
    }

    if (query.error) {
        return (
            <ContentFull>
                <p className="text-red-600">
                    {query.error.message ?? "Failed to load report"}
                </p>
            </ContentFull>
        );
    }

    if (!query.data || query.data.clients.length === 0) {
        return (
            <ContentFull>
                <div className="text-center py-12 text-gray-500">
                    <p className="text-lg font-medium">No time entries found</p>
                    <p className="mt-1 text-sm">
                        No tracked time found for the selected date range.
                    </p>
                </div>
            </ContentFull>
        );
    }

    return (
        <div className="space-y-4">
            <ContentFull>
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-sm font-medium text-gray-500">
                            Total Hours
                        </span>
                        <span className="ml-2 text-2xl font-semibold text-zinc-950">
                            {formatDurationHumanReadable(query.data.grandTotalMinutes)}
                        </span>
                    </div>
                    <SecondaryCompactButton
                        onClick={() => {
                            triggerCsvDownload(
                                generateFullCsv(query.data!.clients),
                                `time-tracking-${startDate}-to-${endDate}.csv`,
                            );
                        }}>
                        Export All CSV
                    </SecondaryCompactButton>
                </div>
            </ContentFull>
            <ReportTable
                clients={query.data.clients}
                grandTotalMinutes={query.data.grandTotalMinutes}
                startDate={startDate}
                endDate={endDate}
            />
        </div>
    );
};

const ReportTable: React.FC<{
    clients: ClientGroup[];
    grandTotalMinutes: number;
    startDate: string;
    endDate: string;
}> = ({ clients, grandTotalMinutes, startDate, endDate }) => {
    return (
        <div className={`overflow-auto ${defaultCardStyle}`}>
            <table className="min-w-full">
                <thead>
                    <tr>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            Client
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left w-28">
                            Date
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-right w-24">
                            Duration
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            User
                        </th>
                        <th className="py-2 px-3 bg-gray-200 text-gray-600 uppercase text-xs text-left">
                            Notes
                        </th>
                        <th className="py-2 px-3 bg-gray-200 w-16" />
                    </tr>
                </thead>
                <tbody>
                    {clients.map(client => (
                        <ClientRows
                            key={client.clientId}
                            client={client}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr className="bg-gray-100 font-semibold border-t border-gray-300">
                        <td className="py-2 px-3">Total</td>
                        <td className="py-2 px-3" />
                        <td className="py-2 px-3 text-right tabular-nums">
                            {formatDurationHumanReadable(grandTotalMinutes)}
                        </td>
                        <td className="py-2 px-3" />
                        <td className="py-2 px-3" />
                        <td className="py-2 px-3" />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

const ClientRows: React.FC<{
    client: ClientGroup;
    startDate: string;
    endDate: string;
}> = ({ client, startDate, endDate }) => {
    const [expanded, setExpanded] = React.useState(false);

    return (
        <React.Fragment>
            <tr
                className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                onClick={() => setExpanded(!expanded)}>
                <td className="py-2.5 px-3">
                    <div className="flex items-center space-x-2">
                        <span className="text-gray-400 text-xs w-4 flex-shrink-0">
                            {expanded ? "\u25BC" : "\u25B6"}
                        </span>
                        <span className="text-sm font-medium text-zinc-950">
                            {client.clientName}
                        </span>
                    </div>
                </td>
                <td className="py-2.5 px-3" />
                <td className="py-2.5 px-3 text-right tabular-nums text-sm font-medium">
                    {formatDurationHumanReadable(client.totalMinutes)}
                </td>
                <td className="py-2.5 px-3" />
                <td className="py-2.5 px-3" />
                <td className="py-2.5 px-3 text-right" onClick={e => e.stopPropagation()}>
                    <SecondaryCompactButton
                        onClick={() => {
                            triggerCsvDownload(
                                generateClientCsv(client.entries),
                                buildClientCsvFilename(
                                    client.clientName,
                                    startDate,
                                    endDate,
                                ),
                            );
                        }}>
                        CSV
                    </SecondaryCompactButton>
                </td>
            </tr>
            {expanded
                ? client.entries.map((entry, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-3" />
                          <td className="py-1.5 px-3 text-sm text-gray-700">
                              {formatEntryDate(entry.date)}
                          </td>
                          <td className="py-1.5 px-3 text-sm text-right tabular-nums text-gray-700">
                              {formatDurationHumanReadable(entry.durationMinutes)}
                          </td>
                          <td className="py-1.5 px-3 text-sm text-gray-700">
                              {entry.userName}
                          </td>
                          <td className="py-1.5 px-3 text-sm text-gray-500">
                              {entry.note ?? ""}
                          </td>
                          <td className="py-1.5 px-3" />
                      </tr>
                  ))
                : null}
        </React.Fragment>
    );
};
